import { describeMutationError } from "@api/mutationErrors";
import { useSessionKey } from "@auth/sessionStore";
import { BoxOverlay } from "@perception/BoxOverlay";
import type { SourceSize } from "@perception/fixtureBoxes";
import { StillFrame } from "@perception/StillFrame";
import type { FixtureBox, Room } from "@rooms/roomsClient";
import { useRoomBaseline } from "@rooms/roomsQueries";
import {
  Button,
  cn,
  Dialog,
  FieldError,
  SegmentedControl,
} from "@scribenote/brian-design-system";
import { useState } from "react";

import {
  type DocEdit,
  type DoorAreasChange,
  MAX_DOOR_AREAS,
  type MapDocument,
  type MapOutside,
  type MapSegment,
  type MapSpace,
  OUTSIDE_KINDS,
  OUTSIDE_LABEL,
} from "./clinicMapClient";
import {
  useHeldStill,
  useRoomDoorAreas,
  useSetRoomDoorAreas,
} from "./clinicMapQueries";
import { MapCanvas } from "./MapCanvas";
import {
  doorAt,
  doorsOf,
  freeDoorCell,
  neighbours,
  wallStretches,
} from "./mapGeometry";
import { doorLabels, type RoomsById, spaceName } from "./mapWords";

type Source = DoorAreasChange["drawnOn"];

interface NewDoor {
  to: MapSpace | null;
  outside: MapOutside | null;
}

/**
 * Draws a room's map doors on its camera's picture (BRI-263). Choosing where a
 * new door leads puts it on the map on the wall the room shares with that space
 * (decided 2026-10-27: real cameras show one or two floor corners, too few to
 * place it from the picture); a wall click is asked for only when the room has
 * more than one such wall.
 */
export const DoorAreasDialog = ({
  space,
  room,
  doc,
  rooms,
  dirty,
  onDocChange,
  onSaveMap,
  onClose,
}: {
  space: MapSpace;
  room: Room;
  doc: MapDocument;
  rooms: RoomsById;
  /** The map has changes not saved yet; door areas name doors on the saved map. */
  dirty: boolean;
  onDocChange: DocEdit;
  onSaveMap: () => Promise<string | null>;
  onClose: () => void;
}) => {
  const sessionKey = useSessionKey();
  const baseline = useRoomBaseline(sessionKey, room.id, true);
  const still = useHeldStill(sessionKey, room.id);
  const existing = useRoomDoorAreas(sessionKey, room.id);
  const setAreas = useSetRoomDoorAreas(sessionKey);

  const [chosen, setChosen] = useState<Source | null>(null);
  // Chosen once both have answered, so a late baseline can't swap the picture
  // after boxes were drawn on the still.
  const settled = !baseline.isPending && !still.isPending;
  const source: Source | null =
    chosen ??
    (!settled
      ? null
      : baseline.data
        ? "baseline"
        : still.data
          ? "still"
          : null);
  const url =
    source === "baseline"
      ? baseline.data?.url
      : source === "still"
        ? still.data?.url
        : undefined;
  const [loaded, setLoaded] = useState<{
    url: string;
    size: SourceSize;
  } | null>(null);
  const size =
    source === "still" && still.data?.width && still.data.height
      ? { width: still.data.width, height: still.data.height }
      : loaded?.url === url
        ? loaded?.size
        : undefined;
  const saved = existing.data;
  const fits =
    saved != null &&
    size !== undefined &&
    saved.width === size.width &&
    saved.height === size.height;
  const mismatch = saved != null && size !== undefined && !fits;

  const roomDoors = doorsOf(doc, space);
  const onWalls = new Set(roomDoors.map((d) => d.uuid));
  const offWalls = fits
    ? saved.doors.filter((d) => !onWalls.has(d.doorUuid)).length
    : 0;
  const [areas, setAreasState] = useState<Map<string, FixtureBox> | null>(null);
  if (areas === null && size && existing.isSuccess)
    setAreasState(
      new Map(
        fits
          ? saved.doors
              .filter((d) => onWalls.has(d.doorUuid))
              .map((d) => [d.doorUuid, d.box])
          : [],
      ),
    );
  const [pending, setPending] = useState<FixtureBox | null>(null);
  const [redraw, setRedraw] = useState<string | null>(null);
  const [picking, setPicking] = useState<
    (NewDoor & { stretches: MapSegment[] }) | null
  >(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const labels = doorLabels(doc, space, rooms);
  const full = (areas?.size ?? 0) >= MAX_DOOR_AREAS;
  const forget = () => {
    setPending(null);
    setRedraw(null);
    setPicking(null);
  };
  const assign = (door: string, box: FixtureBox) => {
    setAreasState(new Map(areas ?? []).set(door, box));
    setPending(null);
    setRedraw(null);
    setPicking(null);
  };
  const place = (cell: MapSegment, target: NewDoor) => {
    if (!pending) return;
    if (doorAt(doc, cell)) {
      setProblem(
        "There is a door there already. Click a free part of the wall.",
      );
      return;
    }
    const uuid = crypto.randomUUID();
    const door = {
      uuid,
      segment: cell,
      kind: "door" as const,
      outside: target.to ? null : target.outside,
    };
    onDocChange((current) => ({ ...current, doors: [...current.doors, door] }));
    setProblem(null);
    assign(uuid, pending);
  };
  const newDoor = (target: NewDoor) => {
    const free = wallStretches(doc, space, target.to).flatMap((stretch) => {
      const cell = freeDoorCell(doc, stretch);
      return cell ? [{ stretch, cell }] : [];
    });
    const [only] = free;
    if (free.length === 1 && only) place(only.cell, target);
    else if (free.length > 1)
      setPicking({ ...target, stretches: free.map((f) => f.stretch) });
    else setProblem("There is no room on that wall for another door.");
  };
  const outerWalls = wallStretches(doc, space, null).length > 0;

  const save = async () => {
    if (!areas || !source) return;
    setSaving(true);
    setProblem(null);
    const mapProblem = dirty ? await onSaveMap() : null;
    if (mapProblem) {
      setProblem(mapProblem);
      setSaving(false);
      return;
    }
    setAreas.mutate(
      {
        roomUuid: room.id,
        change: {
          drawnOn: source,
          stillReference:
            source === "still" ? (still.data?.reference ?? null) : null,
          doors: [...areas]
            .filter(([door]) => onWalls.has(door))
            .map(([doorUuid, box]) => ({ doorUuid, box })),
        },
      },
      { onSuccess: onClose, onSettled: () => setSaving(false) },
    );
  };

  const noPicture =
    !baseline.isPending && !still.isPending && !baseline.data && !still.data;

  return (
    <Dialog
      open
      onClose={onClose}
      size="takeover"
      dismissable={!saving}
      title={`Doors of ${room.name}`}
      footer={
        <div className="flex items-center justify-end gap-2">
          {dirty && (
            <span className="mr-auto text-sm text-annotation">
              Saving also saves your map changes.
            </span>
          )}
          <Button variant="ghost" disabled={saving} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="accent"
            busy={saving}
            disabled={!areas || !source || pending !== null}
            onClick={() => void save()}
          >
            Save doors
          </Button>
        </div>
      }
    >
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="flex min-w-0 flex-col gap-3">
          {baseline.data && still.data && (
            <SegmentedControl<Source>
              aria-label="Picture to draw on"
              size="sm"
              value={source ?? "baseline"}
              options={[
                { value: "baseline", label: "Empty-room baseline" },
                { value: "still", label: "Live picture" },
              ]}
              onValueChange={(value) => {
                setChosen(value);
                setAreasState(null);
                forget();
              }}
            />
          )}
          {noPicture && (
            <p className="text-sm text-ink-body">
              No picture from this room&rsquo;s camera yet. Its doors can still
              go on the map with the door tool.
            </p>
          )}
          {url && (
            <StillFrame
              url={url}
              onLoad={(natural) => setLoaded({ url, size: natural })}
            >
              {size && areas && (
                <BoxOverlay
                  size={size}
                  tone="door"
                  label="Door drawing"
                  draft={pending}
                  disabled={full && !redraw}
                  boxes={[...areas].map(([door, box]) => ({
                    key: door,
                    box,
                    label: labels.get(door) ?? "",
                  }))}
                  onDraw={(box) =>
                    redraw ? assign(redraw, box) : setPending(box)
                  }
                />
              )}
            </StillFrame>
          )}
          {source === "still" && (
            <Button
              size="sm"
              variant="ghost"
              className="self-start"
              onClick={() => {
                forget();
                void still.refetch();
              }}
            >
              Use a newer picture
            </Button>
          )}
          {picking && (
            <div className="flex flex-col gap-2">
              <p className="text-sm text-ink-body">
                {room.name} has more than one wall{" "}
                {picking.to
                  ? `with ${spaceName(picking.to, rooms)}`
                  : "to the outside"}
                . Click the one this door is on.
              </p>
              <MapCanvas
                doc={doc}
                rooms={rooms}
                tool="door"
                onWallCell={(cell) => place(cell, picking)}
                allowedStretches={picking.stretches}
                className="max-h-[50vh]"
              />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4">
          {existing.isError && (
            <div className="flex items-center gap-2">
              <FieldError>Couldn&rsquo;t load the saved doors.</FieldError>
              <Button
                size="sm"
                variant="outline"
                onClick={() => void existing.refetch()}
              >
                Try again
              </Button>
            </div>
          )}
          {offWalls > 0 && (
            <p className="text-sm text-ink-attention">
              {offWalls === 1
                ? "One saved door area is"
                : `${offWalls} saved door areas are`}{" "}
              for doors no longer on this room&rsquo;s walls. Saving drops them.
            </p>
          )}
          {full && !pending && (
            <p className="text-sm text-annotation">
              A room holds at most {MAX_DOOR_AREAS} door areas.
            </p>
          )}
          {mismatch && (
            <p className="text-sm text-ink-attention">
              The saved doors were drawn on a picture of another size, from
              another camera. Draw them again.
            </p>
          )}
          {pending ? (
            <div className="flex flex-col gap-2">
              <h3 className="font-display text-base text-ink">
                Which door is this?
              </h3>
              {roomDoors.map((door) => (
                <Button
                  key={door.uuid}
                  size="sm"
                  variant="outline"
                  onClick={() => assign(door.uuid, pending)}
                >
                  {labels.get(door.uuid)}
                  {areas?.has(door.uuid) && " (replace)"}
                </Button>
              ))}
              <p className="font-mono text-mono-caps text-annotation">
                A new door
              </p>
              {neighbours(doc, space).map((other) => (
                <Button
                  key={other.uuid}
                  size="sm"
                  variant="outline"
                  onClick={() => newDoor({ to: other, outside: null })}
                >
                  To {spaceName(other, rooms)}
                </Button>
              ))}
              {outerWalls &&
                OUTSIDE_KINDS.map((outside) => (
                  <Button
                    key={outside}
                    size="sm"
                    variant="outline"
                    onClick={() => newDoor({ to: null, outside })}
                  >
                    Outside: {OUTSIDE_LABEL[outside]}
                  </Button>
                ))}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setPending(null);
                  setPicking(null);
                }}
              >
                Discard the box
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <p className="text-sm text-ink-body">
                {redraw
                  ? `Drag a box round ${labels.get(redraw)} on the picture.`
                  : "Drag a box round each door the camera sees, then say where it leads. Brian puts new doors on the map."}
              </p>
              <ul className="flex flex-col">
                {roomDoors.map((door) => (
                  <li
                    key={door.uuid}
                    className="flex items-center justify-between gap-2 border-b border-rule py-1 last:border-b-0"
                  >
                    <span
                      className={cn(
                        "min-w-0 truncate text-sm",
                        areas?.has(door.uuid) ? "text-ink" : "text-annotation",
                      )}
                    >
                      {labels.get(door.uuid)}
                      {!areas?.has(door.uuid) && " · not drawn"}
                    </span>
                    <span className="flex shrink-0 gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setRedraw(door.uuid)}
                      >
                        {areas?.has(door.uuid) ? "Redraw" : "Draw"}
                      </Button>
                      {areas?.has(door.uuid) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            const next = new Map(areas);
                            next.delete(door.uuid);
                            setAreasState(next);
                          }}
                        >
                          Remove
                        </Button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {problem && <FieldError>{problem}</FieldError>}
          {setAreas.isError && (
            <FieldError>
              {describeMutationError(setAreas.error, "save the doors")}
            </FieldError>
          )}
        </div>
      </div>
    </Dialog>
  );
};
